import { useMemo } from 'react';
import { listOfDealersType } from '../../redux/appConfigReducer';
import { BODY_FILTERS_LIST_NEW } from '../../constants/listOfFilters';
import { cleanModelFamilies } from '../../utils/cleanModelFamilies';
import { parseAvailabilityOptions } from '../../utils/parseAvailabilityOptions';

const isNumber = (value: any): value is number => typeof value === 'number' && !Number.isNaN(value);

const useParseConfigFilters = (remoteConfig: any) => {
  // General app config
  const isTier3page = remoteConfig?.isTier3page ?? false;
  const isImageGalleryOn = remoteConfig?.isImageGalleryAble ?? false;
  const isUrlParamsDynamic = remoteConfig?.isUrlParamsDynamic ?? false;
  const marketType = remoteConfig?.marketType ?? 'USA';
  const pageTitle = remoteConfig?.pageTitle ?? '';
  const pageDescription = remoteConfig?.pageDescription ?? '';
  const commonModelFamilies = remoteConfig?.commonModelFamilies?.length
    ? cleanModelFamilies(remoteConfig?.commonModelFamilies)
    : ['q3', 'q5', 'q7', 'q8', 'a5'];

  // Group of vars that will let us know what filter group to render
  const isVehicleTypeEnabled = remoteConfig?.isVehicleTypeEnabled && 'vehicleType';
  const isBodyTypeEnabled = remoteConfig?.isBodyTypeEnabled && 'bodyType';
  const isModelFamilyEnabled = remoteConfig?.isModelFamilyEnabled && 'modelFamily';
  const isTrimLineEnabled = remoteConfig?.isTrimLineEnabled && 'trimLine';
  const isExteriorColorEnabled = remoteConfig?.isExteriorColorEnabled && 'exteriorColor';
  const isInteriorColorEnabled = remoteConfig?.isInteriorColorEnabled && 'interiorColor';
  const isFuelTypeEnabled = remoteConfig?.isFuelTypeEnabled && 'fuelType';
  const isDriveTrainEnabled = remoteConfig?.isDriveTrainEnabled && 'drivetrain';
  const isWheelSizeEnabled = remoteConfig?.isWheelSizeEnabled && 'wheelSize';
  const isGearboxEnabled = remoteConfig?.isGearboxEnabled && 'gearBox';
  const isModelYearEnabled = remoteConfig?.isModelYearEnabled && 'modelYear';
  const isPriceEnabled = remoteConfig?.isPriceEnabled && 'price';
  const isPowerEnabled = remoteConfig?.isPowerEnabled && 'power';
  const isMileageEnabled = remoteConfig?.isMileageEnabled && 'mileage';
  const isMakeTypeEnabled = remoteConfig?.isMakeTypeEnabled && 'otherMakes';
  const isNonAudiModelsEnabled = 'nonAudiModels';
  const vehicleTypeDisplayOptions = remoteConfig?.vehicleTypeDisplayOptions?.length
    ? remoteConfig?.vehicleTypeDisplayOptions
    : [];
  const availabilityOptions = parseAvailabilityOptions(
    remoteConfig?.['availability-now-label'],
    remoteConfig?.['availability-all-label'],
  );

  const filterTrayState = remoteConfig?.filterTrayState ?? 'open';

  const isPartnerDealersEnabled = remoteConfig?.listOfDealers?.length && 'partnerDealers';

  const listOfFilterSections = [
    isVehicleTypeEnabled,
    isBodyTypeEnabled,
    isModelFamilyEnabled,
    isTrimLineEnabled,
    isExteriorColorEnabled,
    isInteriorColorEnabled,
    isFuelTypeEnabled,
    isDriveTrainEnabled,
    isWheelSizeEnabled,
    isGearboxEnabled,
    isModelYearEnabled,
    isPriceEnabled,
    isPowerEnabled,
    isMileageEnabled,
    isMakeTypeEnabled,
    isPartnerDealersEnabled,
    isNonAudiModelsEnabled,
  ].filter((element) => element);

  // Filters

  const vehicleType = useMemo(() => {
    const typePreset = remoteConfig?.vehicleTypePreset;
    if (typePreset === 'pre-owned-all')
      return { vehicleType: 'used', usedInventoryType: 'has-warranties.all' };
    if (typePreset === 'pre-owned-combined')
      return { vehicleType: 'used', usedInventoryType: 'has-warranties.combined' };
    if (typePreset === 'cpo')
      return { vehicleType: 'used', usedInventoryType: 'has-warranties.yes' };
    if (typePreset === 'pre-owned')
      return { vehicleType: 'used', usedInventoryType: 'has-warranties.no' };
    if (typePreset === 'non-audi')
      return { vehicleType: 'used', usedInventoryType: 'has-warranties.nonAudi' };
    return { vehicleType: 'new', usedInventoryType: 'has-warranties.combined' };
  }, [remoteConfig?.vehicleTypePreset]);

  const nonAudi = remoteConfig?.vehicleTypePreset === 'non-audi' ? 'foreign-brand.yes' : '';

  const parseBodyTypes = (bodyTypes: string[] | null | undefined) => {
    if (!bodyTypes || !bodyTypes?.length) return [];
    return bodyTypes
      .map((bodyType) => {
        if (bodyType === 'allroad_quattro') return 'allroad-quattro';
        return bodyType;
      })
      .filter((bodyType) => BODY_FILTERS_LIST_NEW.includes(bodyType));
  };

  const bodyType = parseBodyTypes(remoteConfig?.bodyType);
  const modelFamily = remoteConfig?.modelFamily
    ? cleanModelFamilies(remoteConfig?.modelFamily)
    : [];
  const trimLine = remoteConfig?.trimLine ?? [];
  const gearBox = remoteConfig?.gearbox ? [remoteConfig?.gearbox] : [];
  const drivetrain = remoteConfig?.driveTrain ? [remoteConfig?.driveTrain] : [];
  const fuelType = remoteConfig?.fuelType ? [remoteConfig?.fuelType] : [];
  const wheelSize = remoteConfig?.wheelSize ? [remoteConfig?.wheelSize] : [];
  const exteriorColor = remoteConfig?.exteriorColor ? [remoteConfig?.exteriorColor] : [];
  const interiorColor = remoteConfig?.interiorColor ? [remoteConfig?.interiorColor] : [];
  const otherMakes = remoteConfig?.makeTypes ?? [];
  const availableInMonths = remoteConfig?.availableInMonths ?? '3';
  const dataSource = remoteConfig?.dataSource ?? 'jdp';
  const nonAudiModels = [];

  const listOfDealers = remoteConfig?.listOfDealers;
  const mainDealer = listOfDealers?.find(
    (dealer: listOfDealersType) => dealer?.isMainDealer,
  )?.dealerId;

  const powerMin = parseInt(remoteConfig?.powerMin, 10);
  const powerMax = parseInt(remoteConfig?.powerMax, 10);
  const mileageMin = 0;
  const mileageMax = parseInt(remoteConfig?.mileageMax, 10);
  const priceMin = parseInt(remoteConfig?.priceMin, 10);
  const priceMax = parseInt(remoteConfig?.priceMax, 10);
  const modelYear = remoteConfig?.modelYear ?? [];
  const preused = remoteConfig?.preused ?? [];

  const { filterPositions, filterPanelStates } = remoteConfig || {};

  const initialT3Filters = {
    vehicleType: vehicleType.vehicleType,
    bodyType,
    modelFamily,
    gearBox,
    drivetrain,
    fuelType,
    wheelSize,
    exteriorColor: [...exteriorColor],
    interiorColor,
    trimLine,
    power: powerMin && powerMax ? [powerMin, powerMax] : [],
    price: isNumber(priceMin) && isNumber(priceMax) ? [priceMin, priceMax] : [],
    mileage: isNumber(mileageMin) && isNumber(mileageMax) ? [mileageMin, mileageMax] : [],
    modelYear,
    usedInventoryType: vehicleType.usedInventoryType,
    availableInMonths: Number(availableInMonths),
    partnerDealers: mainDealer ? [mainDealer] : [],
    nonAudi,
    otherMakes,
    nonAudiModels: [],
    preused,
    // makeTypes
  };

  if (!remoteConfig?.isTier3page) return null;

  const parsedConfig = {
    listOfDealers,
    isImageGalleryOn,
    isUrlParamsDynamic,
    dataSource,
    isTier3page,
    marketType,
    pageTitle,
    pageDescription,
    commonModelFamilies,
    filterTrayState,
    vehicleTypeDisplayOptions,
    filterGroupsToRender: listOfFilterSections,
    tier3Filters: initialT3Filters,
    filterPositions,
    filterPanelStates,
    availabilityOptions,
  };

  return parsedConfig;
};

export default useParseConfigFilters;
